import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ChevronDown, ChevronRight, Info, Plus } from 'lucide-react';
import {
    listSelectableSowCreditPools,
    type SowCreditPoolType,
} from '@wevo/wevo-access/sow-credit-pools';

import { useCreditMutations } from '../../../hooks/useApi';
import type {
    AdminCreditPulseSubscriptionTier,
    CreateSowHistoricalImportPreviewResponse,
    PricingConfigSummary,
    SowSummary,
} from '../../../services/api';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
    formCheckboxListSurfaceClassName,
    formFieldGroupNestedCardClassName,
    formFieldGroupSurfaceClassName,
} from '../../ui/formPrimitives';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';
import { PricingConfigSelectOptionLabel } from './PricingConfigSelectOptionLabel';
import { AddPricingConfigDialog } from './pricingConfigDialogs';
import { readPricingConfigUnitType } from './pricingConfigForm';
import { SowAllocationRowsEditor } from './SowAllocationRowsEditor';
import {
    Actions,
    AUDIT_REASON_FIELD_HINT,
    DialogFrame,
    Field,
    FormErrorAlert,
} from './creditDialogPrimitives';
import { getMutationErrorMessage } from './creditDialogHelpers';
import { getPoolLabel, formatCredits } from './adminCreditAdapters';
import {
    allocationRowsToPoolsPayload,
    anchorDayFromStartDate,
    createAllocationRow,
    describeAllocationResetPolicy,
    getSelectableCreditPoolOptions,
    poolsPayloadIsValid,
    type AllocationRow,
} from './sowAllocationModel';
import {
    filterActivePricingConfigs,
    pickLatestPricingConfigVersions,
    pricingConfigSelectSearchText,
} from '../adminDashboardHelpers';

function todayLocalDateInput(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function pricingConfigTrailingLink(config: PricingConfigSummary) {
    return (
        <a
            href={`/admin/pricing-configs/${config.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-full min-h-9 items-center px-2 text-neutral-500 transition-colors hover:bg-neutral-200 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
            aria-label={`Open ${config.name} in a new tab`}
            title="Open in new tab"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
        >
            <Info className="size-4 shrink-0" aria-hidden />
        </a>
    );
}

function describeHistoricalImportPulseScope(
    subscription: CreateSowHistoricalImportPreviewResponse['reusablePulseSubscriptions'][number],
) {
    if (subscription.scope === 'team' && subscription.teamId !== null) {
        return `Team #${subscription.teamId}`;
    }
    if (subscription.companyId !== null) {
        return `Company #${subscription.companyId}`;
    }
    return subscription.scope;
}

function buildSowPricingConfigSelectOptions(
    pricingConfigs: PricingConfigSummary[],
    unitMode: 'credit' | 'token',
    showAllActiveVersions: boolean,
    preservePricingConfigId?: string,
): PricingConfigSummary[] {
    const activePricingConfigs = filterActivePricingConfigs(pricingConfigs);
    const versionScoped = showAllActiveVersions
        ? activePricingConfigs
        : pickLatestPricingConfigVersions(activePricingConfigs);
    const forUnitType = versionScoped.filter(
        (config) => readPricingConfigUnitType(config) === unitMode,
    );
    const preserveId = preservePricingConfigId?.trim();
    if (!preserveId) {
        return forUnitType;
    }
    const preserved = pricingConfigs.find((config) => config.id === preserveId);
    if (preserved && !forUnitType.some((config) => config.id === preserved.id)) {
        return [preserved, ...forUnitType];
    }
    return forUnitType;
}

function SowPricingConfigField({
    dialogOpen,
    pricingConfigs,
    unitMode,
    pricingConfigId,
    onPricingConfigIdChange,
    onAddNew,
    preservePricingConfigId,
}: {
    dialogOpen: boolean;
    pricingConfigs: PricingConfigSummary[];
    unitMode: 'credit' | 'token';
    pricingConfigId: string;
    onPricingConfigIdChange: (nextValue: string) => void;
    onAddNew: () => void;
    preservePricingConfigId?: string;
}) {
    const [showAllActiveVersions, setShowAllActiveVersions] = useState(false);

    useEffect(() => {
        if (!dialogOpen) return;
        setShowAllActiveVersions(false);
    }, [dialogOpen]);

    const pricingConfigSelectOptions = useMemo(
        () =>
            buildSowPricingConfigSelectOptions(
                pricingConfigs,
                unitMode,
                showAllActiveVersions,
                preservePricingConfigId ?? pricingConfigId,
            ),
        [pricingConfigs, unitMode, showAllActiveVersions, preservePricingConfigId, pricingConfigId],
    );

    useEffect(() => {
        if (!pricingConfigId) return;
        if (!pricingConfigSelectOptions.some((config) => config.id === pricingConfigId)) {
            onPricingConfigIdChange('');
        }
    }, [pricingConfigId, pricingConfigSelectOptions, onPricingConfigIdChange]);

    return (
        <Field label="Pricing Configuration" required>
            <Select
                ariaLabel="Pricing Configuration"
                searchable
                searchPlaceholder="Search by name or ID"
                options={[
                    { value: '', label: 'Select pricing configuration' },
                    ...pricingConfigSelectOptions.map((config) => ({
                        value: config.id,
                        label: <PricingConfigSelectOptionLabel config={config} />,
                        searchText: pricingConfigSelectSearchText(config),
                        trailing: pricingConfigTrailingLink(config),
                    })),
                    ...(pricingConfigId &&
                    !pricingConfigs.some((config) => config.id === pricingConfigId)
                        ? [
                              {
                                  value: pricingConfigId,
                                  label: `${pricingConfigId} (not in current list)`,
                                  searchText: pricingConfigId,
                              },
                          ]
                        : []),
                    { value: '__new__', label: '+ Add new pricing configuration...' },
                ]}
                value={pricingConfigId}
                onValueChange={(nextValue) => {
                    if (nextValue === '__new__') {
                        onAddNew();
                        return;
                    }
                    onPricingConfigIdChange(nextValue);
                }}
            />
            <label className="mt-1 flex cursor-pointer items-center gap-1.5">
                <input
                    type="checkbox"
                    checked={showAllActiveVersions}
                    onChange={(event) => setShowAllActiveVersions(event.target.checked)}
                    className="size-3.5 rounded border-neutral-300"
                />
                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    Include older versions
                </span>
            </label>
        </Field>
    );
}

export function CreateSowDialog({
    companyId,
    onCreated,
    open,
    onClose,
    pricingConfigs,
    pulseSubscriptionTiers,
    teams,
    teamsLoading = false,
}: {
    companyId: number;
    open: boolean;
    onClose: () => void;
    onCreated?: () => void;
    pricingConfigs: PricingConfigSummary[];
    pulseSubscriptionTiers: AdminCreditPulseSubscriptionTier[];
    teams: Array<{ id: number; name: string }>;
    teamsLoading?: boolean;
}) {
    const { createSow, previewCreateSowHistoricalImport } = useCreditMutations();
    const [name, setName] = useState('');
    const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [unitMode, setUnitMode] = useState<'credit' | 'token'>('credit');
    const [pricingConfigId, setPricingConfigId] = useState('');
    const [allocations, setAllocations] = useState<AllocationRow[]>([
        createAllocationRow('pro_credit'),
    ]);
    const [pulseSubscriptionTierId, setPulseSubscriptionTierId] = useState('');
    const [pulseSeatCount, setPulseSeatCount] = useState('1');
    const [historicalImportSectionOpen, setHistoricalImportSectionOpen] = useState(false);
    const [historicalImportEnabled, setHistoricalImportEnabled] = useState(false);
    const [historicalImportPreview, setHistoricalImportPreview] =
        useState<CreateSowHistoricalImportPreviewResponse | null>(null);
    const [selectedReusablePulseSubscriptionId, setSelectedReusablePulseSubscriptionId] = useState<
        string | null
    >(null);
    const [error, setError] = useState<string | null>(null);
    const [addPricingOpen, setAddPricingOpen] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName('');
        setSelectedTeamIds([]);
        setStartDate('');
        setEndDate('');
        setUnitMode('credit');
        setPricingConfigId('');
        setAllocations([createAllocationRow('pro_credit')]);
        setPulseSubscriptionTierId('');
        setPulseSeatCount('1');
        setHistoricalImportSectionOpen(false);
        setHistoricalImportEnabled(false);
        setHistoricalImportPreview(null);
        setSelectedReusablePulseSubscriptionId(null);
        setError(null);
    }, [open]);

    const selectableCreditPoolOptions = useMemo(() => getSelectableCreditPoolOptions(), []);
    const remainingAddablePoolOptions = useMemo(() => {
        if (unitMode === 'token') {
            return allocations.length === 0 ? getSelectableCreditPoolOptions() : [];
        }
        return getSelectableCreditPoolOptions(allocations.map((row) => row.poolType));
    }, [allocations, unitMode]);

    const selectableOptionsForAllocation = (rowKey: string) =>
        getSelectableCreditPoolOptions(
            allocations.filter((row) => row.key !== rowKey).map((row) => row.poolType),
        );

    useEffect(() => {
        const defaultCreditPoolType = selectableCreditPoolOptions[0]?.value ?? 'pro_credit';
        const allowedCreditPoolTypes = new Set(
            selectableCreditPoolOptions.map((option) => option.value),
        );
        setAllocations((rows) =>
            unitMode === 'token'
                ? rows.map((row) => ({ ...row, poolType: 'token' }))
                : rows.map((row) =>
                      allowedCreditPoolTypes.has(row.poolType)
                          ? row
                          : { ...row, poolType: defaultCreditPoolType as SowCreditPoolType },
                  ),
        );
    }, [selectableCreditPoolOptions, unitMode]);

    useEffect(() => {
        const derivedAnchorDay = anchorDayFromStartDate(startDate);
        setAllocations((rows) =>
            rows.map((row) =>
                row.anchorDayManuallyEdited ? row : { ...row, anchorDay: derivedAnchorDay },
            ),
        );
    }, [startDate]);

    const toggleTeam = (teamId: number) => {
        setSelectedTeamIds((prev) =>
            prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId],
        );
    };

    const addAllocationRow = () => {
        if (remainingAddablePoolOptions.length === 0) return;
        setAllocations((rows) => [
            ...rows,
            createAllocationRow(
                remainingAddablePoolOptions[0].value as SowCreditPoolType,
                startDate,
            ),
        ]);
    };

    const poolsPayload = useMemo(
        () => allocationRowsToPoolsPayload(allocations, unitMode),
        [allocations, unitMode],
    );
    const poolsValid = poolsPayloadIsValid(poolsPayload);

    const needsPulseSubscription = useMemo(() => {
        if (unitMode === 'token') return true;
        return allocations.some((row) => row.poolType === 'pulse_credit');
    }, [unitMode, allocations]);

    const parsedPulseSeatCount = Number(pulseSeatCount);
    const selectedPricingConfig = useMemo(
        () => pricingConfigs.find((config) => config.id === pricingConfigId),
        [pricingConfigs, pricingConfigId],
    );
    const pricingConfigUnitType = selectedPricingConfig
        ? readPricingConfigUnitType(selectedPricingConfig)
        : null;
    const pricingMatchesUnitType =
        Boolean(pricingConfigId) &&
        Boolean(selectedPricingConfig) &&
        pricingConfigUnitType === unitMode;
    const pricingUnitTypeMismatch =
        Boolean(pricingConfigId) &&
        Boolean(selectedPricingConfig) &&
        pricingConfigUnitType !== null &&
        pricingConfigUnitType !== unitMode;
    const historicalImportPreviewReady =
        unitMode === 'credit' &&
        Boolean(startDate) &&
        Boolean(endDate) &&
        Boolean(pricingConfigId) &&
        pricingMatchesUnitType &&
        selectedTeamIds.length > 0;
    const historicalImportPreviewHasReusablePulseSubscription = Boolean(
        selectedReusablePulseSubscriptionId,
    );
    const requiresNewPulseSubscription =
        needsPulseSubscription &&
        !(
            historicalImportEnabled &&
            historicalImportPreview?.summary.hasPulseSpend &&
            historicalImportPreviewHasReusablePulseSubscription
        );
    const pulseSubscriptionValid =
        !requiresNewPulseSubscription ||
        (Boolean(pulseSubscriptionTierId) &&
            Number.isFinite(parsedPulseSeatCount) &&
            parsedPulseSeatCount >= 1);

    const allocationsHaveUniquePoolTypes = useMemo(() => {
        if (unitMode === 'token') {
            return allocations.length <= 1;
        }
        const poolTypes = allocations.map((row) => row.poolType);
        return new Set(poolTypes).size === poolTypes.length;
    }, [allocations, unitMode]);

    useEffect(() => {
        setHistoricalImportPreview(null);
        setSelectedReusablePulseSubscriptionId(null);
    }, [historicalImportEnabled, startDate, endDate, pricingConfigId, selectedTeamIds, unitMode]);

    useEffect(() => {
        if (!historicalImportPreview) {
            return;
        }
        if (historicalImportPreview.recommendedPulseSubscriptionId) {
            setSelectedReusablePulseSubscriptionId(
                historicalImportPreview.recommendedPulseSubscriptionId,
            );
            return;
        }
        if (
            historicalImportPreview.reusablePulseSubscriptions.length > 0 &&
            selectedReusablePulseSubscriptionId &&
            !historicalImportPreview.reusablePulseSubscriptions.some(
                (subscription) => subscription.id === selectedReusablePulseSubscriptionId,
            )
        ) {
            setSelectedReusablePulseSubscriptionId(null);
        }
    }, [historicalImportPreview, selectedReusablePulseSubscriptionId]);

    const isFormReady =
        Boolean(name.trim()) &&
        Boolean(startDate) &&
        Boolean(pricingConfigId) &&
        pricingMatchesUnitType &&
        selectedTeamIds.length > 0 &&
        poolsValid &&
        (!requiresNewPulseSubscription || pulseSubscriptionValid) &&
        allocationsHaveUniquePoolTypes;

    const handleHistoricalImportPreview = async () => {
        if (!historicalImportPreviewReady) {
            setError(
                'Historical import preview requires a credit pricing configuration, start date, end date, and at least one team.',
            );
            return;
        }
        setError(null);
        try {
            const preview = await previewCreateSowHistoricalImport.mutateAsync({
                companyId,
                teamIds: selectedTeamIds,
                startDate,
                endDate,
                pricingConfigId,
            });
            setHistoricalImportPreview(preview);
            setSelectedReusablePulseSubscriptionId(preview.recommendedPulseSubscriptionId);
        } catch (err) {
            setHistoricalImportPreview(null);
            setSelectedReusablePulseSubscriptionId(null);
            setError(getMutationErrorMessage(err, 'Could not preview historical import.'));
        }
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!isFormReady) {
            if (pricingUnitTypeMismatch) {
                setError(
                    'Pricing configuration unit type (credits vs tokens) must match SOW unit type.',
                );
                return;
            }
            setError('Fill in all required fields before creating this SOW.');
            return;
        }
        if (historicalImportEnabled) {
            if (!historicalImportPreview) {
                setError('Preview historical import before creating this SOW.');
                return;
            }
            if (
                historicalImportPreview.pulseSubscriptionSelectionRequired &&
                !selectedReusablePulseSubscriptionId
            ) {
                setError('Select one reusable Pulse subscription before creating this SOW.');
                return;
            }
        }

        setError(null);
        try {
            await createSow.mutateAsync({
                companyId,
                name: name.trim(),
                unitMode,
                startDate,
                endDate: endDate || null,
                pricingConfigId,
                teamIds: selectedTeamIds,
                pools: poolsPayload,
                pulseSubscription: requiresNewPulseSubscription
                    ? {
                          pulseSubscriptionTierId,
                          numSeats: Math.floor(parsedPulseSeatCount),
                      }
                    : null,
                historicalImport: historicalImportEnabled
                    ? {
                          previewSignature: historicalImportPreview!.previewSignature,
                          reusablePulseSubscriptionId:
                              selectedReusablePulseSubscriptionId ??
                              historicalImportPreview!.recommendedPulseSubscriptionId,
                      }
                    : null,
            });
            onCreated?.();
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not create SOW.'));
        }
    };

    return (
        <>
            <DialogFrame open={open} onClose={onClose} title="Create New SOW">
                <form className="space-y-4" onSubmit={handleSubmit}>
                    {error ? <FormErrorAlert message={error} /> : null}
                    <Field label="SOW Name" required>
                        <Input
                            placeholder="e.g., Q2 2026 SOW"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Field>
                    <Field label="Unit Type" required>
                        <Select
                            ariaLabel="Unit Type"
                            options={[
                                { value: 'credit', label: 'Credit' },
                                { value: 'token', label: 'Token' },
                            ]}
                            value={unitMode}
                            onValueChange={(nextValue) =>
                                setUnitMode(nextValue as 'credit' | 'token')
                            }
                        />
                    </Field>
                    <SowPricingConfigField
                        dialogOpen={open}
                        pricingConfigs={pricingConfigs}
                        unitMode={unitMode}
                        pricingConfigId={pricingConfigId}
                        onPricingConfigIdChange={setPricingConfigId}
                        onAddNew={() => setAddPricingOpen(true)}
                    />
                    <div className="grid gap-4 md:grid-cols-2">
                        <Field label="Start Date" required>
                            <div className="flex gap-2">
                                <Input
                                    type="date"
                                    aria-label="Start Date"
                                    className="min-w-0 flex-1"
                                    value={startDate}
                                    onChange={(event) => setStartDate(event.target.value)}
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="shrink-0 border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
                                    onClick={() => setStartDate(todayLocalDateInput())}
                                >
                                    Today
                                </Button>
                            </div>
                        </Field>
                        <Field
                            label="End Date"
                            hint="Inclusive end date. Access ends at the very end of this day."
                        >
                            <Input
                                type="date"
                                aria-label="End Date"
                                value={endDate}
                                onChange={(event) => setEndDate(event.target.value)}
                            />
                        </Field>
                    </div>
                    <Field label="Teams" required>
                        <div
                            className={cn(
                                formCheckboxListSurfaceClassName,
                                'max-h-48 space-y-2 overflow-y-auto',
                            )}
                        >
                            {teams.length === 0 ? (
                                <p className="text-sm text-neutral-500">
                                    {teamsLoading
                                        ? 'Loading teams…'
                                        : 'No teams available for this organization.'}
                                </p>
                            ) : (
                                teams.map((team) => (
                                    <label
                                        key={team.id}
                                        className="flex cursor-pointer items-center gap-2 text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedTeamIds.includes(team.id)}
                                            onChange={() => toggleTeam(team.id)}
                                            className="rounded border-neutral-300"
                                        />
                                        <span>{team.name}</span>
                                        <span className="text-xs text-neutral-400">
                                            (#{team.id})
                                        </span>
                                    </label>
                                ))
                            )}
                        </div>
                    </Field>
                    <details
                        open={historicalImportSectionOpen}
                        className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}
                    >
                        <summary
                            className="flex cursor-pointer list-none items-center gap-2 text-sm text-neutral-800"
                            onClick={(event) => {
                                event.preventDefault();
                                setHistoricalImportSectionOpen((current) => !current);
                            }}
                        >
                            {historicalImportSectionOpen ? (
                                <ChevronDown
                                    className="size-4 shrink-0 text-neutral-500"
                                    aria-hidden
                                />
                            ) : (
                                <ChevronRight
                                    className="size-4 shrink-0 text-neutral-500"
                                    aria-hidden
                                />
                            )}
                            Import (Advanced)
                        </summary>
                        <div className="mt-3 space-y-4">
                            <label className="flex items-center gap-2 text-sm text-neutral-800">
                                <input
                                    type="checkbox"
                                    checked={historicalImportEnabled}
                                    onChange={(event) =>
                                        setHistoricalImportEnabled(event.target.checked)
                                    }
                                    className="rounded border-neutral-300"
                                />
                                <span>Import historical records</span>
                            </label>
                            <div className="space-y-3">
                                <p className="text-xs text-neutral-500">
                                    Preview uses this draft SOW&apos;s start date, end date,
                                    selected teams, and pricing configuration.
                                </p>
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
                                    disabled={
                                        !historicalImportEnabled ||
                                        !historicalImportPreviewReady ||
                                        previewCreateSowHistoricalImport.isPending
                                    }
                                    onClick={handleHistoricalImportPreview}
                                >
                                    {previewCreateSowHistoricalImport.isPending
                                        ? 'Previewing…'
                                        : 'Preview historical import'}
                                </Button>
                                {historicalImportEnabled && historicalImportPreview ? (
                                    <div className="space-y-3">
                                        <div className="grid gap-2 md:grid-cols-2">
                                            <div className={formFieldGroupNestedCardClassName}>
                                                <p className="text-sm font-semibold text-neutral-950">
                                                    {historicalImportPreview.summary.proStudyCount}{' '}
                                                    Pro studies
                                                </p>
                                                <p className="text-xs text-neutral-500">
                                                    {formatCredits(
                                                        Number(
                                                            historicalImportPreview.summary
                                                                .totalProCredits,
                                                        ),
                                                    )}{' '}
                                                    Pro credits
                                                </p>
                                            </div>
                                            <div className={formFieldGroupNestedCardClassName}>
                                                <p className="text-sm font-semibold text-neutral-950">
                                                    {historicalImportPreview.summary.pulseCount}{' '}
                                                    Pulse sessions
                                                </p>
                                                <p className="text-xs text-neutral-500">
                                                    {formatCredits(
                                                        Number(
                                                            historicalImportPreview.summary
                                                                .totalPulseCredits,
                                                        ),
                                                    )}{' '}
                                                    Pulse credits
                                                </p>
                                            </div>
                                        </div>
                                        {historicalImportPreview.warnings.map((warning) => (
                                            <div
                                                key={warning.code}
                                                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                                            >
                                                {warning.message}
                                            </div>
                                        ))}
                                        {historicalImportPreview.reusablePulseSubscriptions.length >
                                        0 ? (
                                            <div className="space-y-2">
                                                <p className="text-sm font-semibold text-neutral-950">
                                                    Reuse previous Pulse subscription
                                                </p>
                                                {historicalImportPreview.reusablePulseSubscriptions.map(
                                                    (subscription) => (
                                                        <label
                                                            key={subscription.id}
                                                            className={cn(
                                                                formFieldGroupNestedCardClassName,
                                                                'flex cursor-pointer items-start gap-3',
                                                            )}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name="historical-import-pulse-subscription"
                                                                aria-label={subscription.id}
                                                                checked={
                                                                    selectedReusablePulseSubscriptionId ===
                                                                    subscription.id
                                                                }
                                                                onChange={() =>
                                                                    setSelectedReusablePulseSubscriptionId(
                                                                        subscription.id,
                                                                    )
                                                                }
                                                                className="mt-1"
                                                            />
                                                            <span className="space-y-1 text-sm">
                                                                <span className="block font-semibold text-neutral-950">
                                                                    {subscription.id}
                                                                </span>
                                                                <span className="block text-neutral-600">
                                                                    {describeHistoricalImportPulseScope(
                                                                        subscription,
                                                                    )}{' '}
                                                                    ·{' '}
                                                                    {subscription.startedAt?.slice(
                                                                        0,
                                                                        10,
                                                                    ) ?? 'No start'}{' '}
                                                                    to{' '}
                                                                    {subscription.expiresAt?.slice(
                                                                        0,
                                                                        10,
                                                                    ) ?? 'Open ended'}
                                                                </span>
                                                                <span className="block text-neutral-500">
                                                                    Tier{' '}
                                                                    {
                                                                        subscription.pulseSubscriptionTierId
                                                                    }{' '}
                                                                    · {subscription.numSeats} seats
                                                                    ·{' '}
                                                                    {formatCredits(
                                                                        Number(
                                                                            subscription.totalCredits,
                                                                        ),
                                                                    )}{' '}
                                                                    credits
                                                                </span>
                                                            </span>
                                                        </label>
                                                    ),
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </details>
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                                <span className="text-sm font-semibold text-neutral-950">
                                    {unitMode === 'token' ? 'Token pools' : 'Credit pools'}
                                    <span className="text-red-600" aria-hidden="true">
                                        {' '}
                                        *
                                    </span>
                                </span>
                                {unitMode === 'credit' ? (
                                    <p className="text-xs text-neutral-500">
                                        Each credit type can only be added once.
                                    </p>
                                ) : null}
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
                                onClick={addAllocationRow}
                                disabled={remainingAddablePoolOptions.length === 0}
                            >
                                + Add pool
                            </Button>
                        </div>
                        <SowAllocationRowsEditor
                            unitMode={unitMode}
                            rows={allocations}
                            labelPrefix="Pool"
                            getSelectableOptions={selectableOptionsForAllocation}
                            onRemoveRow={(key) =>
                                setAllocations((rows) =>
                                    rows.length <= 1 ? rows : rows.filter((row) => row.key !== key),
                                )
                            }
                            onUpdateRow={(key, patch) =>
                                setAllocations((rows) =>
                                    rows.map((row) =>
                                        row.key === key ? { ...row, ...patch } : row,
                                    ),
                                )
                            }
                        />
                    </div>
                    {needsPulseSubscription ? (
                        <div className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                            <p className="text-sm font-semibold text-neutral-950">
                                Pulse subscription
                                <span className="text-red-600" aria-hidden="true">
                                    {' '}
                                    *
                                </span>
                            </p>
                            {requiresNewPulseSubscription ? (
                                <div className="grid gap-4 md:grid-cols-2">
                                    <Field label="Subscription type" required>
                                        <Select
                                            ariaLabel="Subscription type"
                                            options={[
                                                { value: '', label: 'Select subscription type' },
                                                ...pulseSubscriptionTiers.map((tier) => ({
                                                    value: tier.id,
                                                    label: tier.name,
                                                })),
                                            ]}
                                            value={pulseSubscriptionTierId}
                                            onValueChange={setPulseSubscriptionTierId}
                                        />
                                    </Field>
                                    <Field label="Pulse seats" required>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={pulseSeatCount}
                                            onChange={(event) =>
                                                setPulseSeatCount(event.target.value)
                                            }
                                        />
                                    </Field>
                                </div>
                            ) : (
                                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                                    Historical import will reuse the selected previous Pulse
                                    subscription instead of creating a new one from this form.
                                </div>
                            )}
                        </div>
                    ) : null}
                    <Actions onCancel={onClose}>
                        <Button type="submit" disabled={createSow.isPending || !isFormReady}>
                            {createSow.isPending ? 'Creating…' : 'Create SOW'}
                        </Button>
                    </Actions>
                </form>
            </DialogFrame>
            <AddPricingConfigDialog
                open={addPricingOpen}
                onClose={() => setAddPricingOpen(false)}
                onCreated={(config) => setPricingConfigId(config.id)}
                initialUnitType={unitMode}
            />
        </>
    );
}

export function EditSowDialog({
    open,
    onClose,
    sow,
    pricingConfigs,
    teams,
    teamsLoading = false,
}: {
    open: boolean;
    onClose: () => void;
    sow: SowSummary;
    pricingConfigs: PricingConfigSummary[];
    teams: Array<{ id: number; name: string }>;
    teamsLoading?: boolean;
}) {
    const { archiveSow, updateSow } = useCreditMutations();
    const [name, setName] = useState(sow.name);
    const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
    const [startDate, setStartDate] = useState(sow.startDate?.slice(0, 10) ?? '');
    const [endDate, setEndDate] = useState(sow.endDate?.slice(0, 10) ?? '');
    const [pricingConfigId, setPricingConfigId] = useState(sow.pricingConfigId);
    const [reason, setReason] = useState('');
    const [newAllocations, setNewAllocations] = useState<AllocationRow[]>([]);
    const [archiveConfirm, setArchiveConfirm] = useState('');
    const [addPricingOpen, setAddPricingOpen] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const existingCreditPools = useMemo(() => sow.CreditPools ?? [], [sow.CreditPools]);

    const existingCreditPoolTypeValues = useMemo(
        () => existingCreditPools.map((pool) => pool.poolType),
        [existingCreditPools],
    );
    const existingCreditPoolTypeSet = useMemo(
        () => new Set(existingCreditPoolTypeValues),
        [existingCreditPoolTypeValues],
    );

    const selectableTeams = useMemo(() => {
        const byId = new Map<number, { id: number; name: string }>();
        for (const team of teams) {
            byId.set(team.id, team);
        }
        for (const link of sow.SowTeams ?? []) {
            if (!byId.has(link.teamId)) {
                byId.set(link.teamId, {
                    id: link.teamId,
                    name: link.Team?.name?.trim() || `Team ${link.teamId}`,
                });
            }
        }
        return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
    }, [teams, sow.SowTeams]);

    const toggleTeam = (teamId: number) => {
        setSelectedTeamIds((prev) =>
            prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId],
        );
    };

    useEffect(() => {
        if (!open) return;
        setName(sow.name);
        setSelectedTeamIds((sow.SowTeams ?? []).map((link) => link.teamId));
        setStartDate(sow.startDate?.slice(0, 10) ?? '');
        setEndDate(sow.endDate?.slice(0, 10) ?? '');
        setPricingConfigId(sow.pricingConfigId);
        setReason('');
        setNewAllocations([]);
        setArchiveConfirm('');
        setFormError(null);
        setAddPricingOpen(false);
    }, [open, sow.id, sow.name, sow.startDate, sow.endDate, sow.pricingConfigId, sow.SowTeams]);

    useEffect(() => {
        const derivedAnchorDay = anchorDayFromStartDate(startDate);
        setNewAllocations((rows) =>
            rows.map((row) =>
                row.anchorDayManuallyEdited ? row : { ...row, anchorDay: derivedAnchorDay },
            ),
        );
    }, [startDate]);

    const selectedPricingConfig = useMemo(
        () => pricingConfigs.find((config) => config.id === pricingConfigId),
        [pricingConfigs, pricingConfigId],
    );
    const pricingConfigUnitType = selectedPricingConfig
        ? readPricingConfigUnitType(selectedPricingConfig)
        : null;
    const pricingUnitTypeMismatch =
        Boolean(pricingConfigId) &&
        Boolean(selectedPricingConfig) &&
        pricingConfigUnitType !== null &&
        pricingConfigUnitType !== sow.unitMode;

    const remainingAddablePoolOptions = useMemo(
        () =>
            getSelectableCreditPoolOptions([
                ...existingCreditPoolTypeValues,
                ...newAllocations.map((row) => row.poolType),
            ]),
        [existingCreditPoolTypeValues, newAllocations],
    );
    const poolsForNewAllocations = useMemo(
        () => allocationRowsToPoolsPayload(newAllocations, 'credit'),
        [newAllocations],
    );
    const hasDraftNewPools = poolsForNewAllocations.length > 0;
    const newPoolsValid = !hasDraftNewPools || poolsPayloadIsValid(poolsForNewAllocations);

    const selectableOptionsForAllocation = (rowKey: string, currentPoolType: SowCreditPoolType) => {
        const options = getSelectableCreditPoolOptions([
            ...existingCreditPoolTypeValues,
            ...newAllocations.filter((row) => row.key !== rowKey).map((row) => row.poolType),
        ]);
        if (!options.some((option) => option.value === currentPoolType)) {
            const currentPool = listSelectableSowCreditPools('credit').find(
                (pool) => pool.type === currentPoolType,
            );
            if (currentPool && !existingCreditPoolTypeSet.has(currentPool.type)) {
                options.unshift({
                    value: currentPool.type,
                    label: currentPool.label,
                });
            }
        }
        return options;
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const trimmedName = name.trim();
        const trimmedReason = reason.trim();
        if (!startDate.trim() || !trimmedName || !endDate.trim() || !trimmedReason) {
            setFormError('Fill in all required fields before saving.');
            return;
        }
        if (pricingUnitTypeMismatch) {
            setFormError(
                'Pricing configuration unit type (credits vs tokens) must match this SOW’s unit type.',
            );
            return;
        }
        if (!pricingConfigId.trim()) {
            setFormError('Select a pricing configuration.');
            return;
        }
        if (!newPoolsValid) {
            setFormError('Fill in each new pool completely before saving.');
            return;
        }
        if (selectedTeamIds.length === 0) {
            setFormError('Select at least one team for this SOW.');
            return;
        }
        setFormError(null);
        try {
            await updateSow.mutateAsync({
                sowId: sow.id,
                request: {
                    name: trimmedName,
                    startDate,
                    endDate,
                    pricingConfigId,
                    teamIds: selectedTeamIds,
                    newPools: poolsForNewAllocations,
                    reason: trimmedReason,
                },
            });
            onClose();
        } catch (err) {
            setFormError(getMutationErrorMessage(err, 'Could not save SOW changes.'));
        }
    };

    const isArchived = sow.state === 'archived';

    return (
        <>
            <DialogFrame
                open={open}
                onClose={onClose}
                title="Edit SOW"
                description="Saving updates the SOW and writes an audit record. Fields marked with * are required; the reason is stored for auditing."
            >
                <form className="space-y-4" onSubmit={handleSubmit}>
                    {formError ? <FormErrorAlert message={formError} /> : null}
                    <Field label="SOW Name" required>
                        <Input
                            required
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </Field>
                    <SowPricingConfigField
                        dialogOpen={open}
                        pricingConfigs={pricingConfigs}
                        unitMode={sow.unitMode}
                        pricingConfigId={pricingConfigId}
                        onPricingConfigIdChange={setPricingConfigId}
                        onAddNew={() => setAddPricingOpen(true)}
                        preservePricingConfigId={sow.pricingConfigId}
                    />
                    <Field label="Start Date" required>
                        <div className="flex gap-2">
                            <Input
                                required
                                type="date"
                                className="min-w-0 flex-1"
                                value={startDate}
                                onChange={(event) => setStartDate(event.target.value)}
                            />
                            <Button
                                type="button"
                                variant="outline"
                                className="shrink-0 border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
                                onClick={() => setStartDate(todayLocalDateInput())}
                            >
                                Today
                            </Button>
                        </div>
                    </Field>
                    <Field
                        label="End Date"
                        required
                        hint="Inclusive end date. Access ends at the very end of this day."
                    >
                        <Input
                            required
                            type="date"
                            aria-label="End Date"
                            value={endDate}
                            onChange={(event) => setEndDate(event.target.value)}
                        />
                    </Field>
                    <Field
                        label="Teams"
                        required
                        hint="Only teams in this organization can be linked."
                    >
                        <div
                            className={cn(
                                formCheckboxListSurfaceClassName,
                                'max-h-48 space-y-2 overflow-y-auto',
                            )}
                        >
                            {selectableTeams.length === 0 ? (
                                <p className="text-sm text-neutral-500">
                                    {teamsLoading
                                        ? 'Loading teams…'
                                        : 'No teams available for this organization.'}
                                </p>
                            ) : (
                                selectableTeams.map((team) => (
                                    <label
                                        key={team.id}
                                        className="flex cursor-pointer items-center gap-2 text-sm"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedTeamIds.includes(team.id)}
                                            onChange={() => toggleTeam(team.id)}
                                            className="rounded border-neutral-300"
                                        />
                                        <span>{team.name}</span>
                                        <span className="text-xs text-neutral-400">
                                            ({team.id})
                                        </span>
                                    </label>
                                ))
                            )}
                        </div>
                    </Field>
                    {sow.unitMode === 'credit' ? (
                        <div className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                            <div className="space-y-1">
                                <p className="text-sm font-semibold text-neutral-950">
                                    Existing pools
                                </p>
                                <p className="text-xs text-neutral-500">
                                    Existing pool rows are read-only here. Use this dialog only to
                                    append brand-new pool types.
                                </p>
                            </div>
                            <div className="space-y-3">
                                {existingCreditPools.map((pool, index) => (
                                    <div
                                        key={pool.id ?? `${pool.poolType}-${index}`}
                                        className={formFieldGroupNestedCardClassName}
                                    >
                                        <div className="flex flex-wrap items-start justify-between gap-2">
                                            <div>
                                                <p className="font-medium text-neutral-950">
                                                    {getPoolLabel(pool.poolType)}
                                                </p>
                                                <p className="text-sm text-neutral-600">
                                                    Initial amount: {pool.initialAmount}
                                                </p>
                                            </div>
                                            <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                                                Read only
                                            </span>
                                        </div>
                                        <p className="mt-2 text-xs text-neutral-500">
                                            {describeAllocationResetPolicy(pool.resetPolicy)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {sow.unitMode === 'credit' ? (
                        <div className={cn(formFieldGroupSurfaceClassName, 'space-y-4')}>
                            <div className="flex items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-semibold text-neutral-950">
                                        Add pools
                                    </p>
                                    <p className="text-xs text-neutral-500">
                                        Only pool types not already on this SOW can be added.
                                    </p>
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-50"
                                    onClick={() =>
                                        setNewAllocations((rows) =>
                                            remainingAddablePoolOptions.length === 0
                                                ? rows
                                                : [
                                                      ...rows,
                                                      createAllocationRow(
                                                          remainingAddablePoolOptions[0]
                                                              .value as SowCreditPoolType,
                                                          startDate,
                                                      ),
                                                  ],
                                        )
                                    }
                                    disabled={remainingAddablePoolOptions.length === 0}
                                >
                                    <Plus className="mr-1 size-4" />
                                    Add pool
                                </Button>
                            </div>
                            {newAllocations.length === 0 &&
                            remainingAddablePoolOptions.length === 0 ? (
                                <p className="text-sm text-neutral-500">
                                    All available credit pool types are already on this SOW.
                                </p>
                            ) : newAllocations.length === 0 ? (
                                <p className="text-sm text-neutral-500">No new pools added yet.</p>
                            ) : (
                                <SowAllocationRowsEditor
                                    unitMode="credit"
                                    rows={newAllocations}
                                    labelPrefix="New pool"
                                    allowRemoveSingleRow
                                    getSelectableOptions={selectableOptionsForAllocation}
                                    onRemoveRow={(key) =>
                                        setNewAllocations((rows) =>
                                            rows.filter((row) => row.key !== key),
                                        )
                                    }
                                    onUpdateRow={(key, patch) =>
                                        setNewAllocations((rows) =>
                                            rows.map((row) =>
                                                row.key === key ? { ...row, ...patch } : row,
                                            ),
                                        )
                                    }
                                />
                            )}
                        </div>
                    ) : null}
                    <Field label="Reason for edit" required hint={AUDIT_REASON_FIELD_HINT}>
                        <Input
                            required
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                        />
                    </Field>
                    <div className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
                        <div>
                            <p className="text-sm font-semibold text-amber-950">Archive SOW</p>
                            <p className="mt-1 text-sm text-amber-800">
                                Archiving soft-deletes this SOW. It will stay available in the admin
                                portal when archived SOWs are included.
                            </p>
                        </div>
                        <Field label="Type ARCHIVE to confirm">
                            <Input
                                value={archiveConfirm}
                                onChange={(event) => setArchiveConfirm(event.target.value)}
                                disabled={isArchived || archiveSow.isPending}
                            />
                        </Field>
                        <Button
                            type="button"
                            variant="destructive"
                            disabled={
                                isArchived || archiveConfirm !== 'ARCHIVE' || archiveSow.isPending
                            }
                            onClick={async () => {
                                setFormError(null);
                                try {
                                    await archiveSow.mutateAsync(sow.id);
                                    onClose();
                                } catch (err) {
                                    setFormError(
                                        getMutationErrorMessage(err, 'Could not archive SOW.'),
                                    );
                                }
                            }}
                        >
                            {archiveSow.isPending
                                ? 'Archiving...'
                                : isArchived
                                  ? 'Already archived'
                                  : 'Archive SOW'}
                        </Button>
                    </div>
                    <Actions onCancel={onClose}>
                        <Button type="submit" disabled={updateSow.isPending}>
                            {updateSow.isPending ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Actions>
                </form>
            </DialogFrame>
            <AddPricingConfigDialog
                open={addPricingOpen}
                onClose={() => setAddPricingOpen(false)}
                onCreated={(config) => setPricingConfigId(config.id)}
                initialUnitType={sow.unitMode}
                pricingConfigs={pricingConfigs}
            />
        </>
    );
}
